import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { getExtractionStepsByMail } from '../../features/information-extraction/api/getExtractionStepsByMail.js';

// Chain-of-thought shapes mirror what the server writes via addIntrospectMeta
// (search-products.ts / correctAndCompleteCompany.ts). Kept local so the client never
// imports server code (same convention as STEP_META below).
interface ProductCandidate {
  materialNumber: string | null;
  productGroup: string | null;
  material: string | null;
  deliveryCondition: string | null;
  verdict: string;
  reason: string;
}

interface CompanyCandidate {
  label: string;
  debitor: string;
  score: number;
  verdict: string;
}

interface MatchDecision {
  kind: 'product' | 'company';
  search?: Record<string, unknown> | null;
  selectedMaterialNumber?: string | null;
  selectedName?: string | null;
  selectedDebitor?: string | null;
  matchType?: string | null;
  confidence?: number | null;
  reasoning?: string | null;
  matchReason?: string | null;
  isNewCompany?: boolean;
  warnings?: string[];
  gateNote?: string | null;
  candidates?: Array<ProductCandidate | CompanyCandidate>;
  // Bulk-order length → piece-count derivation (e.g. "200 m ÷ 12,5 m = 16 Stück"), present
  // only when the request gave a total length without a per-piece length.
  quantityCalc?: { note: string; usedDefault?: boolean } | null;
}

interface StepStatus {
  operation: string;
  startedAt: string | null;
  finishedAt: string | null;
  hasError: boolean;
  decisions?: MatchDecision[];
}

// Display metadata for each pipeline step, in run order. The `label` MUST match the
// introspection operation name set in runExtractionByContext.ts (EXTRACTION_STEP_LABELS) —
// kept here separately so the client never imports server code.
const STEP_META: Array<{ label: string; icon: string; description: string }> = [
  {
    label: 'Prüfen ob Anfrage',
    icon: 'help',
    description: 'Wir prüfen, ob die Mail überhaupt eine Produktanfrage ist.',
  },
  {
    label: 'Klartext-Dokument erstellen',
    icon: 'description',
    description: 'Mail und Anhänge werden in ein einheitliches, lesbares Dokument übersetzt — ohne etwas zu deuten.',
  },
  {
    label: 'Produktdaten auslesen',
    icon: 'straighten',
    description: 'Aus dem Dokument lesen wir Maße, Längen, Mengen und Werkstoffe der einzelnen Positionen.',
  },
  {
    label: 'Firma erkennen',
    icon: 'business',
    description: 'Wir bestimmen die anfragende Firma und gleichen sie mit den Stammdaten ab.',
  },
  {
    label: 'Produkte zuordnen',
    icon: 'inventory_2',
    description: 'Jede Position wird einem Artikel aus dem Katalog zugeordnet (Material-Nummer).',
  },
  {
    label: 'Bestelldaten erfassen',
    icon: 'event',
    description: 'Bestellnummer, Wunschtermin und Referenzen werden erfasst.',
  },
];

// Step under which the canonical ("Klartext") document is offered inline — must match the
// label in STEP_META above.
const CANONICAL_STEP_LABEL = 'Klartext-Dokument erstellen';

type StepState = 'done' | 'running' | 'error' | 'pending';

const pct = (n: number | null | undefined): string => (typeof n === 'number' ? `${Math.round(n * 100)}%` : '—');

const MATCH_TYPE_LABEL: Record<string, string> = {
  exact: 'Exakter Treffer',
  compatible: 'Kompatibel',
  conditional: 'Bedingt – Prüfung empfohlen',
  none: 'Kein Treffer',
};

const candidateMarker = (selected: boolean) => html`
  <x-icon class="text-sm shrink-0 ${selected ? 'text-success' : 'text-base-content/30'}">
    ${selected ? 'check_circle' : 'cancel'}
  </x-icon>
`;

const renderProductCandidate = (c: ProductCandidate) => {
  const selected = c.verdict === 'selected';
  return html`
    <li class="flex gap-2 ${selected ? '' : 'opacity-70'}">
      ${candidateMarker(selected)}
      <div class="min-w-0">
        <div class="font-mono text-xs font-medium">
          ${c.materialNumber ?? '—'}
          ${c.productGroup ? html`<span class="font-sans text-base-content/50">· ${c.productGroup}</span>` : nothing}
        </div>
        ${c.material
          ? html`<div class="text-xs text-base-content/50">
              ${c.material}${c.deliveryCondition ? ` · ${c.deliveryCondition}` : ''}
            </div>`
          : nothing}
        <div class="text-xs text-base-content/70">${c.reason}</div>
      </div>
    </li>
  `;
};

const renderCompanyCandidate = (c: CompanyCandidate) => {
  const selected = c.verdict === 'selected';
  return html`
    <li class="flex gap-2 ${selected ? '' : 'opacity-70'}">
      ${candidateMarker(selected)}
      <div class="min-w-0">
        <div class="text-xs">${c.label}</div>
        <div class="font-mono text-xs text-base-content/50">Debitor ${c.debitor} · Ähnlichkeit ${pct(c.score)}</div>
      </div>
    </li>
  `;
};

const renderProductSearchLine = (search: Record<string, unknown> | null | undefined) => {
  if (!search) return nothing;
  const num = (k: string): number | null => {
    const v = search[k];
    return typeof v === 'number' ? v : null;
  };
  const dims = [num('outerDiameter1'), num('outerDiameter2'), num('wallThickness')].filter(v => v !== null).join(' × ');
  const material = typeof search.material === 'string' ? search.material : null;
  if (!dims && !material) return nothing;
  return html`<div class="text-xs text-base-content/50">
    Gesucht: ${material ?? ''}${material && dims ? ' · ' : ''}${dims ? `${dims} mm` : ''}
  </div>`;
};

const renderCompanySearchLine = (search: Record<string, unknown> | null | undefined) => {
  if (!search) return nothing;
  const str = (k: string): string | null => (typeof search[k] === 'string' ? (search[k] as string) : null);
  const parts = [str('name'), str('address'), str('city')].filter(Boolean);
  if (!parts.length) return nothing;
  return html`<div class="text-xs text-base-content/50">Gesucht: ${parts.join(' · ')}</div>`;
};

const renderDecision = (d: MatchDecision) => {
  const isProduct = d.kind === 'product';
  const hasMatch = isProduct ? !!d.selectedMaterialNumber : !!d.selectedName;
  const headline = isProduct
    ? d.selectedMaterialNumber
      ? `Mat ${d.selectedMaterialNumber}`
      : 'Kein passender Artikel'
    : (d.selectedName ?? 'Keine Firma zugeordnet');
  const reasoning = d.reasoning ?? d.matchReason ?? null;
  // Selected candidate(s) first, rejected after.
  const candidates = [...(d.candidates ?? [])].sort(
    (a, b) => (a.verdict === 'selected' ? 0 : 1) - (b.verdict === 'selected' ? 0 : 1),
  );

  return html`
    <div class="flex flex-col gap-2 rounded-lg bg-base-200/50 p-3">
      <div class="flex flex-wrap items-center gap-2 text-xs">
        <x-icon class="text-sm ${hasMatch ? 'text-success' : 'text-warning'}">
          ${isProduct ? 'inventory_2' : 'business'}
        </x-icon>
        <span class="font-medium">${headline}</span>
        ${isProduct && d.matchType
          ? html`<span class="badge badge-ghost badge-xs">${MATCH_TYPE_LABEL[d.matchType] ?? d.matchType}</span>`
          : nothing}
        ${typeof d.confidence === 'number'
          ? html`<span class="font-mono text-base-content/40">${pct(d.confidence)}</span>`
          : nothing}
      </div>
      ${reasoning ? html`<div class="text-xs text-base-content/70">${reasoning}</div>` : nothing}
      ${d.quantityCalc?.note
        ? html`<div
            class="flex items-start gap-1 text-xs ${d.quantityCalc.usedDefault
              ? 'text-warning'
              : 'text-base-content/70'}"
          >
            <x-icon class="text-sm shrink-0">calculate</x-icon><span>${d.quantityCalc.note}</span>
          </div>`
        : nothing}
      ${isProduct ? renderProductSearchLine(d.search) : renderCompanySearchLine(d.search)}
      ${d.gateNote ? html`<div class="text-xs text-warning">${d.gateNote}</div>` : nothing}
      ${(d.warnings ?? []).map(
        w => html`<div class="flex items-start gap-1 text-xs text-warning">
          <x-icon class="text-sm shrink-0">warning</x-icon><span>${w}</span>
        </div>`,
      )}
      ${candidates.length
        ? html`<div>
            <div class="mb-1 text-[11px] uppercase tracking-wide text-base-content/40">
              Betrachtete Optionen (${candidates.length})
            </div>
            <ul class="flex flex-col gap-1.5">
              ${candidates.map(c =>
                isProduct ? renderProductCandidate(c as ProductCandidate) : renderCompanyCandidate(c as CompanyCandidate),
              )}
            </ul>
          </div>`
        : nothing}
    </div>
  `;
};

/**
 * Friendly, non-technical "Nachvollziehen" view of the extraction pipeline. Renders the
 * fixed list of steps and overlays the live status from the introspection tree, so it works
 * both during a running extraction (steps fill in) and afterwards (full timeline).
 *
 * While `live` is true it polls the step status every 1.5s so the steps tick in as they run.
 */
export const ExtractionTimeline = stateful(
  (props: { mailID: string; live?: boolean; canonicalDocument?: string | null; onDownloadCanonical?: () => void }) => {
    const { mailID, live = false, canonicalDocument, onDownloadCanonical } = props;

  // Poll while the extraction is running so steps fill in live; stop once finished.
  // setState takes a value (no updater), so we set a fresh timestamp each tick.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setTick(Date.now()), 1500);
    return () => clearInterval(id);
  }, [live]);

  const { value: stepsJson } = useQuery(() => getExtractionStepsByMail(mailID)(), [mailID, live, tick]);

  let steps: StepStatus[] = [];
  try {
    steps = stepsJson ? (JSON.parse(stepsJson) as StepStatus[]) : [];
  } catch {
    steps = [];
  }
  const byOperation = new Map(steps.map(s => [s.operation, s]));

  const stateOf = (label: string): StepState => {
    const s = byOperation.get(label);
    if (!s) return 'pending';
    if (s.hasError) return 'error';
    if (s.finishedAt) return 'done';
    return 'running';
  };

  const durationOf = (label: string): string | null => {
    const s = byOperation.get(label);
    if (!s?.startedAt || !s.finishedAt) return null;
    const ms = new Date(s.finishedAt).getTime() - new Date(s.startedAt).getTime();
    if (ms < 0) return null;
    return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
  };

  const marker = (state: StepState, icon: string) => {
    if (state === 'done')
      return html`<x-icon class="text-success">check_circle</x-icon>`;
    if (state === 'error') return html`<x-icon class="text-error">error</x-icon>`;
    if (state === 'running')
      return html`<span class="loading loading-spinner loading-sm text-primary"></span>`;
    return html`<x-icon class="text-base-content/25">${icon}</x-icon>`;
  };

  return html`
    <ol class="relative flex flex-col gap-1">
      ${STEP_META.map((step, i) => {
        const state = stateOf(step.label);
        const duration = durationOf(step.label);
        const isLast = i === STEP_META.length - 1;
        const dim = state === 'pending';
        const decisions = byOperation.get(step.label)?.decisions ?? [];
        return html`
          <li class="flex gap-3">
            <div class="flex flex-col items-center">
              <div class="flex items-center justify-center w-6 h-6 shrink-0">${marker(state, step.icon)}</div>
              ${isLast
                ? nothing
                : html`<div class="w-px flex-1 my-0.5 ${state === 'done' ? 'bg-success/40' : 'bg-base-300'}"></div>`}
            </div>
            <div class="flex-1 min-w-0 pb-3 ${dim ? 'opacity-50' : ''}">
              <div class="flex items-center gap-2">
                <span class="font-medium text-sm">${step.label}</span>
                ${duration ? html`<span class="text-xs text-base-content/40 font-mono">${duration}</span>` : nothing}
              </div>
              <div class="text-xs text-base-content/60 leading-relaxed">${step.description}</div>
              ${decisions.length
                ? html`<details class="mt-2">
                    <summary
                      class="inline-flex cursor-pointer select-none items-center gap-1 text-xs font-medium text-primary"
                    >
                      <x-icon class="text-sm">lightbulb</x-icon>
                      Begründung anzeigen${decisions.length > 1 ? ` (${decisions.length})` : ''}
                    </summary>
                    <div class="mt-2 flex flex-col gap-2">${decisions.map(d => renderDecision(d))}</div>
                  </details>`
                : nothing}
              ${step.label === CANONICAL_STEP_LABEL && canonicalDocument
                ? html`<details class="mt-2">
                    <summary
                      class="inline-flex cursor-pointer select-none items-center gap-1 text-xs font-medium text-primary"
                    >
                      <x-icon class="text-sm">description</x-icon>
                      Klartext anzeigen
                    </summary>
                    <div class="mt-2 flex flex-col gap-2">
                      ${onDownloadCanonical
                        ? html`<div class="flex justify-end">
                            <button class="btn btn-ghost btn-xs gap-1" @click=${() => onDownloadCanonical()}>
                              <x-icon class="text-sm">download</x-icon> Als Markdown
                            </button>
                          </div>`
                        : nothing}
                      <pre
                        class="whitespace-pre-wrap wrap-break-word text-xs leading-relaxed bg-base-200/50 border border-base-300 rounded-lg p-3 font-mono"
                      >${canonicalDocument}</pre>
                    </div>
                  </details>`
                : nothing}
            </div>
          </li>
        `;
      })}
    </ol>
  `;
  },
);
